/* global gon */
import { Controller } from "@hotwired/stimulus";
import "instantsearch.css/themes/reset.css";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import instantsearch from "instantsearch.js";
import { menu, searchBox, configure } from "instantsearch.js/es/widgets";
import { connectHits } from "instantsearch.js/es/connectors";
import LazyLoad from "vanilla-lazyload";

export default class extends Controller {
  static targets = ["searchBox", "filters", "hits", "originalContent"];

  initialize() {
    if (!this.hasSearchBoxTarget) return;

    this.initAlgolia();
    this.initWidgets();
    this.lazyLoad = new LazyLoad();
  }

  connect() {
    if (gon.search === false) return;
    if (this.search) this.search.start();
  }

  disconnect() {
    if (this.search) this.search.dispose();
  }

  initAlgolia() {
    const searchClient = algoliasearch(
      gon.global.algolia.application_id,
      gon.search.secured_api_key,
    );

    const hitsContainer = this.hitsTarget;
    const originalContent = this.originalContentTarget;

    this.search = instantsearch({
      indexName: this.index,
      routing: true,
      searchFunction: (helper) => {
        if (helper.state.query === "" && helper.state.facetsRefinements.length === 0) {
          hitsContainer.style.display = "none";
          originalContent.style.display = "";
        } else {
          hitsContainer.style.display = "";
          originalContent.style.display = "none";
        }

        helper.search();
      },
      searchClient,
    });
  }

  initWidgets() {
    this.search.addWidget(
      configure({
        hitsPerPage: this.hitsPerPage,
        facetFilters: gon.search.facetFilters,
      }),
    );

    this.search.addWidget(
      searchBox({
        container: this.searchBoxTarget,
        placeholder: "Search here...",
        showLoadingIndicator: false,
        templates: {
          submit:
            '<svg class="icon icon-search button-icon"><use xlink:href="#icon-search"></use></svg>',
        },
      }),
    );

    this.search.addWidget(
      menu({
        container: this.filtersTarget,
        attribute: "kind",
        limit: 4,
        sortBy: ["name:asc"],
        templates: {
          item: `<a class="{{cssClasses.link}}" href="{{url}}">
            <svg class="icon icon-{{icon}}"><use xlink:href="#icon-{{icon}}"></use></svg>
            <span class="{{cssClasses.label}}">{{label}}</span>
          </a>`,
        },
        transformItems(items) {
          const labels = {
            image: "images",
            video: "videos",
          };
          return items.map((item) => ({
            ...item,
            icon: item.label,
            label: labels[item.label],
          }));
        },
      }),
    );

    const renderHits = (renderArgs, isFirstRender) => {
      const { hits, results, widgetParams } = renderArgs;

      if (isFirstRender) {
        this.setupSentinel(widgetParams);
        return;
      }

      const hitsHtml = this.hitsTemplate(hits, this.hitTemplate);

      if (results.page === 0) {
        widgetParams.container.innerHTML = hitsHtml;
      } else {
        widgetParams.container.insertAdjacentHTML("beforeend", hitsHtml);
      }

      this.lazyLoad.update();
      this.lastRenderArgs = renderArgs;
    };

    const customHits = connectHits(renderHits, () => {});

    this.search.addWidget(
      customHits({
        container: this.hitsTarget,
      }),
    );
  }

  setupSentinel(widgetParams) {
    this.sentinel = document.createElement("div");
    this.sentinel.classList.add("clients-wall-sentinel");

    widgetParams.container.insertAdjacentElement("afterend", this.sentinel);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.showMore();
        }
      });
    });

    observer.observe(this.sentinel);
  }

  showMore() {
    if (!this.lastRenderArgs) return;

    const { page, nbPages } = this.lastRenderArgs.results;
    if (page === nbPages) {
      this.sentinel.innerText = "You've reached the end... Come back later!";
    } else {
      this.sentinel.innerText = "Loading more...";
      this.search.helper.setPage(page + 1).search();
    }
  }

  get hitsTemplate() {
    return (hits, template) => hits.map(template).join("");
  }

  get hitTemplate() {
    if (this.indexKey === "newvisual") {
      return (hit) => `
        <a class="clients-wall-item" href="${hit.clients_path}" data-url="${
          hit.clients_attachment_url
        }">
          ${
            hit.medium_type === "Video"
              ? `<svg class="icon icon-play"><use xlink:href="#icon-play"></use></svg>`
              : ""
          }
          <img class="lazy" data-src="${hit.thumbnail}">
          <span class="clients-wall-item-label">${hit.attachable_title}</span>
        </a>`;
    } else if (this.indexKey === "talents" || this.indexKey === "tags") {
      return (hit) => `
        <a class="clients-wall-item clients-wall-item-fixed" href="${hit.clients_path}">
          <img class="lazy" data-src="${hit.thumbnail}">
          <span class="clients-wall-item-label">${
            hit._highlightResult.name ? hit._highlightResult.name.value : hit.name
          }</span>
        </a>`;
    }

    return (hit) => `<li class="clients-search-results-item">
      <a href="${hit.clients_path}" class="clients-search-results-item-link">${
        hit._highlightResult.name ? hit._highlightResult.name.value : hit.name
      }</a>
      ${
        hit._snippetResult &&
        hit._snippetResult.text &&
        hit._snippetResult.text.matchLevel === "full"
          ? `<div class="clients-search-results-item-snippet">${hit._snippetResult.text.value}</div>`
          : ""
      }
      ${
        hit._snippetResult &&
        hit._snippetResult.tags &&
        hit._snippetResult.tags.filter((t) => t.matchLevel === "full").length
          ? `<div class="clients-tags-list"><ul>${hit._snippetResult.tags
              .filter((t) => t.matchLevel === "full")
              .map((t) => `<li class="clients-tags-list-tag">${t.value}</li>`)
              .join(" ")}</ul></div>`
          : ""
      }
    </li>`;
  }

  get hitsPerPage() {
    if (!this._hitsPerPage) this._hitsPerPage = this.itemsPerRow * 5;
    return this._hitsPerPage;
  }

  get indexKey() {
    if (gon.search && gon.search.index) return gon.search.index;
    return "newvisual";
  }

  get index() {
    return gon.global.algolia.indices[this.indexKey];
  }

  get itemsPerRow() {
    const minWidth = 200;
    return Math.floor(this.element.offsetWidth / minWidth);
  }
}
